import React, { useEffect, useState } from "react";
import api, { money } from "@/lib/api";
import { useI18n } from "@/context/PrefsContext";
import { PageHeader, StatCard, TableShell, EmptyState } from "@/components/ui-kit";
import { DollarSign, AlertCircle, CheckCircle2, FileWarning, PieChart as PieIcon, Users, Sparkles, TrendingUp } from "lucide-react";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, PieChart, Pie, Cell,
} from "recharts";

const COLORS = ["#5A189A", "#7B2CBF", "#D4AF37", "#9D4EDD", "#C77DFF", "#3B82F6", "#10B981", "#EC4899"];

export default function Reports() {
  const [data, setData] = useState(null);
  const { t } = useI18n();
  useEffect(() => { api.get("/reports").then(({ data }) => setData(data)).catch(() => {}); }, []);
  if (!data) return null;

  const maxCount = Math.max(1, ...(data.popular_services || []).map((p) => p.count));

  return (
    <div data-testid="reports-page">
      <PageHeader title="Reports" subtitle="Business insights across revenue, jobs, and clients." />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <StatCard icon={DollarSign} label={t("Total Revenue")} value={money(data.total_revenue)} accent="#16a34a" testid="report-revenue" />
        <StatCard icon={CheckCircle2} label={t("Jobs Completed")} value={data.jobs_completed} accent="#5A189A" testid="report-jobs" />
        <StatCard icon={AlertCircle} label={t("Outstanding")} value={money(data.outstanding)} accent="#ef4444" testid="report-outstanding" />
        <StatCard icon={FileWarning} label={t("Open Invoices")} value={data.outstanding_count} accent="#D4AF37" testid="report-open-invoices" />
      </div>

      {/* Monthly Revenue */}
      <Card title={t("Monthly Revenue")} icon={TrendingUp}>
        {data.monthly_revenue.length === 0 ? <ChartEmpty icon={TrendingUp} text={t("Revenue trends will appear once payments are recorded.")} /> : (
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={data.monthly_revenue}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0ebf7" vertical={false} />
              <XAxis dataKey="month" stroke="#9b8bb5" fontSize={12} />
              <YAxis stroke="#9b8bb5" fontSize={12} />
              <Tooltip formatter={(v) => money(v)} />
              <Bar dataKey="revenue" fill="#5A189A" radius={[8, 8, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        )}
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
        <Card title={t("Top Customers by Revenue")} icon={Users}>
          {data.top_clients.length === 0 ? <ChartEmpty icon={Users} text={t("No customer revenue yet. Record payments to see your top customers.")} /> : (
            <Donut data={data.top_clients.map((c) => ({ name: c.name, value: c.revenue }))} />
          )}
        </Card>

        <Card title={t("Revenue by Cleaning Service")} icon={PieIcon}>
          {data.services_breakdown.length === 0 ? <ChartEmpty icon={PieIcon} text={t("No service revenue yet. Invoiced services will break down here.")} /> : (
            <Donut data={data.services_breakdown} />
          )}
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
        <Card title={t("Most Popular Services")} icon={Sparkles}>
          {(!data.popular_services || data.popular_services.length === 0) ? <ChartEmpty icon={Sparkles} text={t("Schedule jobs to see which services are booked most.")} /> : (
            <div className="space-y-4 pt-2">
              {data.popular_services.map((p, i) => (
                <div key={p.name}>
                  <div className="flex items-center justify-between text-sm mb-1.5">
                    <span className="font-medium text-[#1a1523]">{p.name}</span>
                    <span className="text-[#6b647a]">{p.count} {p.count !== 1 ? t("jobs ").trim() : t("job")}</span>
                  </div>
                  <div className="h-2.5 rounded-full bg-[#f1ebfa] overflow-hidden">
                    <div className="h-full rounded-full transition-all" style={{ width: `${(p.count / maxCount) * 100}%`, background: COLORS[i % COLORS.length] }} />
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>

        <Card title={t("Outstanding Invoices")} icon={FileWarning}>
          {data.outstanding_invoices.length === 0 ? <ChartEmpty icon={CheckCircle2} text={t("No outstanding invoices — all paid up!")} /> : (
            <TableShell headers={["Invoice", "Customer", "Balance"]}>
              {data.outstanding_invoices.map((inv) => (
                <tr key={inv.id} className="border-b border-[#f4f0fa]">
                  <td className="py-3 px-4 text-sm font-medium text-[#5A189A]">{inv.invoice_number}</td>
                  <td className="py-3 px-4 text-sm">{inv.customer_name}</td>
                  <td className="py-3 px-4 text-sm font-medium text-red-500">{money(inv.balance_due)}</td>
                </tr>
              ))}
            </TableShell>
          )}
        </Card>
      </div>
    </div>
  );
}

function Card({ title, icon: Icon, children }) {
  return (
    <div className="bg-white rounded-2xl border border-[#f0ebf7] shadow-[0_4px_18px_-10px_rgba(90,24,154,0.18)] p-6">
      <h3 className="font-display text-xl font-semibold text-[#1a1523] mb-4 flex items-center gap-2">
        <Icon size={18} className="text-[#5A189A]" /> {title}
      </h3>
      {children}
    </div>
  );
}

function Donut({ data }) {
  const total = data.reduce((a, d) => a + Number(d.value || 0), 0);
  return (
    <div className="flex flex-col sm:flex-row items-center gap-4">
      <ResponsiveContainer width="100%" height={230} className="flex-1">
        <PieChart>
          <Pie data={data} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={58} outerRadius={92} paddingAngle={2} stroke="none">
            {data.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
          </Pie>
          <Tooltip formatter={(v) => money(v)} />
        </PieChart>
      </ResponsiveContainer>
      <div className="space-y-2 w-full sm:w-44">
        {data.slice(0, 6).map((d, i) => (
          <div key={i} className="flex items-center justify-between text-xs">
            <span className="flex items-center gap-2 min-w-0">
              <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: COLORS[i % COLORS.length] }} />
              <span className="truncate text-[#6b647a]">{d.name}</span>
            </span>
            <span className="font-semibold text-[#1a1523] ml-2">{total ? Math.round((d.value / total) * 100) : 0}%</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function ChartEmpty({ icon: Icon, text }) {
  return (
    <div className="h-56 flex flex-col items-center justify-center text-center px-4">
      <div className="w-12 h-12 rounded-2xl bg-[#5A189A]/8 text-[#b9a3da] flex items-center justify-center mb-3">
        <Icon size={22} />
      </div>
      <p className="text-sm text-[#9b8bb5] max-w-xs">{text}</p>
    </div>
  );
}
